import { useEffect, useRef, useState } from "react";
import { animated } from "@react-spring/web";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Persona } from "../../models/persona.js";
import "./Speaker.scss";
import { getString } from "../../services/stringsHelpers.js";
import { useLanguage } from "../../container/LanguageContext.jsx";

/**
 * @component
 * @param {Object} args
 * @param {Persona} args.persona
 * @param {string} args.imageSrc
 * @param {"left" | "right"} args.orientation
 * @param {Object} args.springs
 * @param {React.StyleHTMLAttributes<HTMLDivElement> | undefined} args.style
 */
function Speaker({ persona, imageSrc, orientation, springs, style }) {
  const { strings } = useLanguage();
  const [intrinsicHeight, setIntrinsicHeight] = useState(0);
  const imageRef = useRef(/** @type {HTMLImageElement|null} */ (null));

  // NOTE: Add this in the event that the image is loaded before the component
  // loads. Without this, it's possible that the image is already loaded, and
  // the onLoad handler never runs
  useEffect(() => {
    if (imageRef.current && imageRef.current.complete) {
      setIntrinsicHeight(imageRef.current.naturalHeight);
    }
  }, []);
  return (
    <animated.div
      className={classNames("dataviz__speaker", {
        "dataviz__speaker--left": orientation === "left",
        "dataviz__speaker--right": orientation === "right",
      })}
      style={{
        ...style,
        ...springs,
        height: `${(intrinsicHeight / (1080 * 2)) * 100}%`,
      }}
    >
      <img
        src={imageSrc}
        className="dataviz__speaker__img"
        alt={`${persona.name} ${getString("asking a question", strings)}`}
        id={JSON.stringify(persona)}
        ref={imageRef}
        onLoad={(e) =>
          setIntrinsicHeight(
            /** @type {HTMLImageElement} */ (e.target).naturalHeight
          )
        }
      />
    </animated.div>
  );
}

Speaker.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  persona: PropTypes.instanceOf(Persona).isRequired,
  orientation: PropTypes.oneOf(["left", "right"]),
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  springs: PropTypes.object.isRequired,
};

Speaker.defaultProps = {
  orientation: "left",
};

export default Speaker;
